import React, { useState } from "react";
import { Box, Flex, Text, Divider } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { RespondButton, ViewAnswerButton, StatusLabel } from "../styles";

const AvatarCircle: React.FC<{ label: string; bg: string; color: string }> = ({
  label,
  bg,
  color,
}) => (
  <Box
    w="36px"
    h="36px"
    borderRadius="full"
    bg={bg}
    color={color}
    display="flex"
    alignItems="center"
    justifyContent="center"
    fontWeight="bold"
    fontSize="14px"
    flexShrink={0}
  >
    {label}
  </Box>
);

const PendingQnA: React.FC<PendingQnAProps> = ({
  questions = [],
  onRespond,
  isAnswering = false,
}) => {
  const { t } = useTranslation("common");
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const renderPendingQuestions = () => {
    return questions.map((q) => {
      if (!q || !q.id) return null;

      const isExpanded = expandedIds.includes(q?.id);
      const reporterName = q?.createdBy?.fullName ?? t("faq.unknownReporter");
      const reporterInitial = reporterName.charAt(0).toUpperCase();
      const dateStr = q?.createdAt ? q.createdAt.split("T")[0] : t("faq.unknownDate");

      return (
        <Box
          key={q?.id}
          border="1px solid #E2E8F0"
          borderRadius="12px"
          mb={4}
          bg="#FFFFFF"
          boxShadow="0px 1px 4px 0px rgba(0,0,0,0.06)"
          overflow="hidden"
        >
          {/* Thread subject header */}
          <Flex align="center" justify="space-between" px={5} pt={4} pb={3}>
            <Flex align="center" gap={3}>
              <Text fontWeight="700" fontSize="15px" color="#2d384b">
                {q?.title || t("faq.unknownQuestion")}
              </Text>
              <StatusLabel
                bg={q?.answer ? "#3A59D11A" : "#5C66701A"}
                color={q?.answer ? "RoyalBlue" : "#5C6670"}
              >
                {q?.answer
                  ? t("inquiriesTab.queryAnswered")
                  : t("inquiriesTab.pending")}
              </StatusLabel>
            </Flex>

            {!q?.answer ? (
              <RespondButton
                onClick={() => onRespond?.(q?.id)}
                disabled={isAnswering}
                opacity={isAnswering ? 0.6 : 1}
                cursor={isAnswering ? "not-allowed" : "pointer"}
              >
                {t("faq.respondButton")}
              </RespondButton>
            ) : (
              <ViewAnswerButton onClick={() => toggleExpanded(q?.id)}>
                {isExpanded ? t("faq.collapse") : t("faq.viewanswer")}
              </ViewAnswerButton>
            )}
          </Flex>

          <Divider borderColor="#E2E8F0" />

          {/* Journalist's question card */}
          <Box px={5} py={4}>
            <Flex align="flex-start" gap={3}>
              <AvatarCircle label={reporterInitial} bg="#E8E8F0" color="#4b5563" />
              <Box flex={1}>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontWeight="600" fontSize="14px" color="#2d384b">
                    {reporterName}
                  </Text>
                  <Text fontSize="12px" color="#a0aec0">·</Text>
                  <Text fontSize="12px" color="#a0aec0">
                    {dateStr}
                  </Text>
                </Flex>
                <Text fontSize="14px" color="#4b5563" whiteSpace="pre-wrap">
                  {q?.content || t("faq.unknownQuestion")}
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* KPEC answer card */}
          {isExpanded && q.answer && (
            <>
              <Divider borderColor="#E2E8F0" />
              <Box px={5} py={4} bg="#f8f9ff">
                <Flex align="flex-start" gap={3}>
                  <AvatarCircle label="K" bg="#1819B4" color="white" />
                  <Box flex={1}>
                    <Flex align="center" gap={2} mb={1}>
                      <Text fontWeight="600" fontSize="14px" color="#1819B4">
                        KPEC
                      </Text>
                      <Text fontSize="12px" color="#a0aec0">·</Text>
                      <Text fontSize="12px" color="#a0aec0">
                        {t("faq.officialAnswer")}
                      </Text>
                    </Flex>
                    <Text fontSize="14px" color="#2F2F34" whiteSpace="pre-wrap">
                      {q.answer}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </>
          )}
        </Box>
      );
    });
  };

  return (
    <Box border="1px solid #E2E8F0" borderRadius="md" p={4} mb={6} bg="white">
      <Text fontWeight="bold" fontSize="lg" mb={1}>
        {t("faq.pendingQuestions")}
      </Text>
      <Text fontSize="sm" color="gray.500" mb={4}>
        {t("faq.userQuesResponses")}
      </Text>

      {questions.length === 0 ? (
        <Text color="gray.500" fontSize="sm">
          {t("faq.noQuestions")}
        </Text>
      ) : (
        renderPendingQuestions()
      )}
    </Box>
  );
};

export default PendingQnA;
